/**
 * Step 1 of Create → Q&A: the proposed questions in an editable table. The
 * user edits the text, reorders, deletes and adds rows; nothing is answered
 * until they confirm. A question the server could not find verbatim in the
 * document is flagged so it gets a second look.
 */

import { ActionIcon, Badge, Button, Group, Table, Text, Textarea, Tooltip } from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { move, newDraftRow, type DraftQuestion } from "./qaTypes";

interface Props {
  rows: DraftQuestion[];
  onChange: (rows: DraftQuestion[]) => void;
  disabled?: boolean;
}

export function QuestionEditor({ rows, onChange, disabled = false }: Props) {
  const update = (index: number, question: string) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, question } : r)));

  return (
    <>
      <Table verticalSpacing="xs" data-testid="question-editor">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={48}>#</Table.Th>
            <Table.Th>Question</Table.Th>
            <Table.Th w={160}>Asked at</Table.Th>
            <Table.Th w={120} aria-label="Row actions" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, i) => (
            <Table.Tr key={row.key}>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  Q{i + 1}
                </Text>
              </Table.Td>
              <Table.Td>
                <Textarea
                  value={row.question}
                  onChange={(e) => update(i, e.currentTarget.value)}
                  autosize
                  minRows={1}
                  maxRows={6}
                  disabled={disabled}
                  placeholder="Type the question"
                  aria-label={`Question ${i + 1}`}
                  error={row.question.trim() ? undefined : "Empty rows are skipped"}
                />
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {row.source_location && (
                    <Text size="xs" c="dimmed">
                      {row.source_location}
                    </Text>
                  )}
                  {row.added && (
                    <Badge size="xs" variant="light" color="gray">
                      Added
                    </Badge>
                  )}
                  {!row.verbatim && !row.added && (
                    <Tooltip
                      label="This exact wording was not found in the document — check it against the source."
                      multiline
                      w={260}
                    >
                      <Badge
                        size="xs"
                        variant="light"
                        color="orange"
                        leftSection={<IconAlertTriangle size={10} />}
                      >
                        Check wording
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap={2} wrap="nowrap" justify="flex-end">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => onChange(move(rows, i, i - 1))}
                    disabled={disabled || i === 0}
                    aria-label={`Move question ${i + 1} up`}
                  >
                    <IconArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => onChange(move(rows, i, i + 1))}
                    disabled={disabled || i === rows.length - 1}
                    aria-label={`Move question ${i + 1} down`}
                  >
                    <IconArrowDown size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => onChange(rows.filter((_, j) => j !== i))}
                    disabled={disabled}
                    aria-label={`Delete question ${i + 1}`}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconPlus size={16} />}
          onClick={() => onChange([...rows, newDraftRow()])}
          disabled={disabled}
        >
          Add question
        </Button>
      </Group>
    </>
  );
}
